import {priority, wcfclient} from '@priority-software/priority-proto-api/lib';
import {
    TODOLIST,
    USER_NAME_COL,
    DOC_CUR_DATE_COL,
    E_NAME_COL,
    STATUS_FIELD,
    PRIORITY_FIELD,
    MAX_LOAD,
    MANDATORY_COLS
} from './consts';
import {getColIdByName, getFieldValue, getViewColsData} from './utils';
import {
    startForm,
    noDefaultMessageHandling,
    fieldUpdate,
    recordSave,
    fieldSelect,
    recordUndo,
    fetchFormData
} from '../../services/form/form';
import {getAdditionalProps} from '../../services/proto/core/loginProps';
import {callServer} from '../../services/proto/core/server';
import {ServerMethods} from '../../services/proto/core/constants';
import {getUserLogin} from '../../constants/metaData';
import {ISort} from '../../stores/Form/Sort';
import {handleMessage} from '../Messages/actions';
import {Form} from '../../stores/Form/Form';
import {IFilterWithSort} from '../Form/types';
import {applyFilters} from '../Form/actions/filters';
import {startRecordEdit} from '../Form/actions/form';
import {Record} from '../../stores/Form/Record';
import {startEntity} from '../../_shared/GWTBridge/invoke/genPage';
import {isNewApiForm} from '../../_shared/GWTBridge/invoke/netForm';
import {startNewForm} from '../Form/actions/screen';
import {IFilter} from '../../stores/Form/Filter';
import {mapValueByColumnType} from '../../stores/Form/utils';
import {apiLocker, Column} from '../../stores/Form';

export type ToDoListData = {
    form: Form;
    userName: string;
    userNameCol: Column;
    filter: IFilter[];
    viewColumns?: priority.netitems.form.IColumn[];
    curDateColID?: number;
};

type SortToDosDataParams = {
    form: Form;
    filter: IFilter[];
    sortCol: ISort | null;
};

type FormStatusesParams = {
    form: Form;
    record: Record;
};

type UpdateTaskPriorityParams = {
    form: Form;
    record: Record;
    value: string;
};

type UpdateTaskStatusParams = {
    form: Form;
    value: string;
};

const getAssignedToMeFilter = (columnId: number): IFilter[] => [
    {
        Condition: [
            {
                ColumnID: columnId,
                Operator: priority.netitems.shared.ComparisonOperator.Eq,
                Values: [getUserLogin()]
            }
        ],
        Operator: priority.netitems.shared.LogicalOperator.And
    }
];

const getTodoListSort = (columnId: number): ISort[] => [
    {
        ColumnID: columnId,
        Desc: true
    }
];

export const fetchWidgetData = async (
    form: Form,
    columns: Column[],
    userNameColumnID: number,
    curDateCol: number,
    selectedColumnsName: string[]
) => {
    const selectedColumn: Column[] =
        selectedColumnsName?.length &&
        columns?.filter(col =>
            [...new Set([...selectedColumnsName, ...MANDATORY_COLS])]?.includes(col.Name)
        );

    const assignedToMeFilter =
        userNameColumnID && getAssignedToMeFilter(userNameColumnID as number);
    const toDolistSort = curDateCol && getTodoListSort(curDateCol);

    const recordsRs =
        userNameColumnID &&
        (await fetchFormData({
            session: form.session,
            filters: assignedToMeFilter,
            sort: toDolistSort,
            top: MAX_LOAD + 1,
            select: selectedColumn?.map(col => col.ID) as number[]
        }));
    const isRecordsOk = await handleMessage(recordsRs, form.session);
    return {
        isOk: isRecordsOk,
        response: recordsRs,
        filters: assignedToMeFilter,
        selectedColumn
    };
};

export const fecthToDosData = async (selectedColumnsName?: string[]): Promise<ToDoListData> => {
    const startRs: priority.netitems.form.StartRs = await startForm(
        TODOLIST,
        noDefaultMessageHandling,
        [E_NAME_COL]
    );
    const session = startRs.Session;
    const isFormOk = await handleMessage(startRs, session);

    if (isFormOk) {
        const columnsRs = await callServer(
            wcfclient.columns,
            new priority.netitems.form.ColumnsRq({
                Session: startRs.Session,
                Properties: getAdditionalProps()
            }),
            ServerMethods.ColumnsRq
        );

        if (columnsRs) {
            const isColumnOk = await handleMessage(columnsRs, session);
            if (isColumnOk) {
                const form: Form = new Form(TODOLIST, startRs, columnsRs, 0);
                const {Columns} = columnsRs;
                const userNameColumn = Columns.find(col => col.Name == USER_NAME_COL);
                const curDateCol = Columns.find(col => col.Name == DOC_CUR_DATE_COL);
                const requestedColumns = form.keyColumns.length
                    ? [...selectedColumnsName, ...form.keyColumns.map(col => col.Name)]
                    : selectedColumnsName;
                const recordsRs = await fetchWidgetData(
                    form,
                    Columns,
                    userNameColumn.ID,
                    curDateCol?.ID,
                    requestedColumns
                );

                if (recordsRs.response && recordsRs.isOk) {
                    const {Records} = recordsRs.response;
                    form.setRecords(Records);
                    return {
                        curDateColID: curDateCol?.ID,
                        form: form,
                        userName: getUserLogin(),
                        userNameCol: userNameColumn,
                        filter: recordsRs.filters,
                        viewColumns: getViewColsData(recordsRs.selectedColumn, selectedColumnsName)
                    };
                }
            }
        }
    }

    return null;
};

export const getSortedRecords = async ({form, filter, sortCol}: SortToDosDataParams) => {
    form.updateSort([sortCol]);

    const myFilter: IFilterWithSort = {
        Condition: [filter[0].Condition[0]],
        Operator: priority.netitems.shared.LogicalOperator.Or,
        sort: sortCol
    };

    form.updateFilters([myFilter]);
    await applyFilters({
        form,
        top: MAX_LOAD + 1,
        filtersOperator: priority.netitems.shared.LogicalOperator.Or
    });
    return form.records;
};

export const getFormStatuses = async ({form, record}: FormStatusesParams) => {
    if (apiLocker.isLock(form.session)) {
        return null;
    }
    form.setCurrentRow(record.recordIndex);
    const allowed = await startRecordEdit(form, null);
    if (allowed) {
        const ColumnID = getColIdByName(STATUS_FIELD, form.columns);

        const fieldSelectRs = await fieldSelect({
            Session: form.session,
            ColumnID
        });
        const isfieldOk = await handleMessage(fieldSelectRs, form.session);
        if (isfieldOk) {
            const formStatuses = fieldSelectRs.Values;
            return formStatuses;
        }
    }
};

export const updateTaskPriority = async ({form, record, value}: UpdateTaskPriorityParams) => {
    if (apiLocker.isLock(form.session)) {
        return;
    }
    form.setCurrentRow(record.recordIndex);
    const allowed = await startRecordEdit(form, null);
    if (allowed) {
        const column = form.columns.find(column => column.Name === PRIORITY_FIELD);
        const fieldUpdateRs = await fieldUpdate(form.session, mapValueByColumnType(value, column));
        await handleMessage(fieldUpdateRs, form.session);
        form.updateRecordValues(record.recordIndex, fieldUpdateRs.Record?.Fields);
        const saveRecordRs = await recordSave(form.session);
        await handleMessage(saveRecordRs, form.session);
    }
};

export const updateTaskStatus = async ({form, value}: UpdateTaskStatusParams) => {
    const column = form.columns.find(column => column.Name === STATUS_FIELD);
    const fieldUpdateRs = await fieldUpdate(form.session, mapValueByColumnType(value, column));
    await handleMessage(fieldUpdateRs, form.session);
    form.updateRecordValues(form.currentRow, fieldUpdateRs.Record?.Fields);
};

export const saveUpdatedStatus = async (form: Form) => {
    const saveRecordRs = await recordSave(form.session);
    form.updateRecordValues(form.currentRow, saveRecordRs.Record?.Fields);
    const onShowInfo = async (respone: priority.netitems.form.IRecordSaveRs) => {
        form.updateRecordValues(form.currentRow, respone.Record?.Fields);
    };

    const onErrorAccept = async () => {
        const recUndoRes = await recordUndo(form.session, {
            Attachments: priority.netitems.form.AttachmentOptions.Attach_NoData
        });
        form.updateRecordValues(form.currentRow, recUndoRes.Record?.Fields);
    };

    await handleMessage(saveRecordRs, form.session, {onShowInfo, onErrorAccept});
};

export const openToDoListScreen = ({formName, userNameColName, userName}) => {
    const isNewApi = isNewApiForm(formName);
    if (isNewApi) {
        return startNewForm(formName, null, [{fieldName: userNameColName, value: userName}]);
    } else {
        return startEntity(formName, userNameColName, userName);
    }
};

export const doZoom = async ({record, mandatoryColIds, docNumVal}) => {
    const targetEname = getFieldValue(record, mandatoryColIds[E_NAME_COL]).toString();
    const isNewApi = isNewApiForm(targetEname);
    if (isNewApi) {
        await startNewForm(targetEname, docNumVal);
    } else {
        await startEntity(targetEname, null, docNumVal);
    }
};
